import { useAuth0 } from '@auth0/auth0-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Layout } from './components/layout/layout';
import { config } from './config';
import { useGlobalData } from './contexts/GlobalDataProvider';
import MaintenanceWindow from './pages/MaintenanceWindow/MaintenanceWindow';
import { LocalStorageService } from './services/local-storage';
import { SaveAssetJobsSelector } from './store/slices/saveAssetJobsState/SaveAssetJobsState';
import { cacheUtils } from './utils/cacheUtils';

const getGTMId = (env: string) => {
  // console.log('env', env)
  let gtmId = '';
  switch (env) {
    case 'dev01':
      gtmId = 'GTM-5XRRVCD6'; // GTM-WKJGBZ9J
      break;
    case 'qa01':
      gtmId = 'GTM-K3R34W84';
      break;
    case 'prod01':
      gtmId = 'GTM-NM9SXB8G';
      break;
    default:
      console.warn(`${env} doesn’t have a Google Tag Manager ID.`);
      break;
  }
  return gtmId;
};
export const App = () => {
  const location = useLocation();
  const flags = useFlags();
  const isProduction = process.env.NODE_ENV === 'production';
  const { ingestInProgress } = useSelector(SaveAssetJobsSelector);
  const { globalData } = useGlobalData();
  const { user } = useAuth0();
  const tagManagerArgs = {
    gtmId: getGTMId(config.fuelEnvironmentName),
  };
  const [maintenanceWindowModeEnable, setMaintenanceWindowModeEnable] = useState(false);
  const navigate = useNavigate();

  // implement cached version check every page navigation
  useEffect(() => {
    // console.log('location changed', { location,title, isProduction , user});
    doCachedVersionCheck();
    const args = {
      event: 'pageview',
      page: {
        path: location.pathname,
        user: user ? user.name : '',
      },
    };
    // TagManager.dataLayer(tagManagerArgs)
    if (window.dataLayer) {
      window.dataLayer.push(args);
    }
  }, [location]);

  useEffect(() => {
    // console.log('flags changed', { flags, location: location?.pathname });
    const popupsmartBundleId = flags['view-nexa-ui-popupsmart-bundle-id'];
    const uiMaintenanceWindowFlag = flags['UI-enable-maintenance-window'];
    if (popupsmartBundleId) {
      const scriptSrc = 'https://cdn.popupsmart.com/bundle.js';
      const isLoaded = document.querySelectorAll(`[src="${scriptSrc}"]`).length > 0;
      if (!isLoaded) {
        const script = document.createElement('script');
        script.src = scriptSrc;
        script.setAttribute('data-id', popupsmartBundleId);
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      }
    }
    if (
      uiMaintenanceWindowFlag &&
      uiMaintenanceWindowFlag.message &&
      uiMaintenanceWindowFlag.minutes &&
      typeof uiMaintenanceWindowFlag.minutes === 'number'
    ) {
      setMaintenanceWindowModeEnable(true);
    }
  }, [flags]);

  const closingTab = (e) => {
    // console.log('arePopupsShowing', arePopupsShowing);
    if (ingestInProgress) {
      try {
        e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
        // e.returnValue = 'File upload is in progress. If you leave this page, the upload will be interrupted. Are you sure you want to continue?';
        //  window.alert("Sure to leave?");
        const confirmationMessage =
          'File upload is in progress. If you leave this page, the upload will be interrupted. Are you sure you want to continue?';
        (e || window.event).returnValue = confirmationMessage; // Gecko + IE
        return confirmationMessage;
      } catch (e) {}
    }
  };

  useEffect(() => {
    window.removeEventListener('beforeunload', closingTab);
    if (ingestInProgress) {
      window.addEventListener('beforeunload', closingTab);
    }
    return () => {
      window.removeEventListener('beforeunload', closingTab);
    };
  }, [ingestInProgress]);

  useEffect(() => {
    // Clear LocalStorageService
    LocalStorageService.removeItem('FUEL_RETRY_MAP');
    // Google Tag Manager initialization
    if (config.fuelEnvironmentName && getGTMId(config.fuelEnvironmentName) !== '') {
      TagManager.initialize(tagManagerArgs);
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'pageview',
          page: {
            path: location.pathname,
            user: user ? user.name : '',
          },
        });
      }
    }
  }, []);

  const doCachedVersionCheck = async () => {
    // console.log('doCachedVersionCheck');
    // const metaFileUrl = `https://fueltools-fuelui-${config.fuelEnvironmentName}.s3.amazonaws.com/meta.json`;
    const metaFileUrl = `https://${config.subDomainName}.${config.fuelDomainName}/meta.json`;
    await cacheUtils.checkVersion(config.appVersion, metaFileUrl);
  };

  if (Object.keys(flags).length === 0) {
    return null;
  }

  return (
    <>
      {maintenanceWindowModeEnable && <MaintenanceWindow />}
      {!maintenanceWindowModeEnable && <Layout />}
    </>
  );
};
